'use client'

import { memo, useMemo, useState } from 'react'
import { Box, Flex, Text, Icon, Badge, Tooltip, useColorModeValue } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { ChevronDown, ChevronRight, Folder, FolderOpen, ExternalLink } from 'lucide-react'
import { motion } from 'framer-motion'

const styles = {
  folderContainer: css`
    width: 100%;
    overflow-x: auto;
  `,
  folderItem: (isActive, depth) => css`
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    margin-bottom: 4px;
    margin-left: ${depth * 12}px;
    display: flex;
    align-items: center;
  `,
  folderIcon: css`
    margin-right: 8px;
  `,
  folderName: css`
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
  `,
  bookmarkCount: css`
    font-size: 12px;
    font-weight: 500;
  `,
  childrenContainer: css`
    margin-top: 4px;
  `,
  folderHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  `,
  folderTitle: css`
    font-size: 16px;
    font-weight: 600;
  `,
  emptyText: css`
    color: gray;
    font-style: italic;
    padding: 8px 12px;
    font-size: 14px;
  `,
  chevronContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-right: 8px;
  `,
  folderBadge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
  `,
}

// Build folder tree from flat list
const buildFolderTree = (rootFolder, childFolders) => {
  const folderMap = new Map()

  // Add root folder to map
  folderMap.set(rootFolder._id.toString(), {
    ...rootFolder,
    children: [],
    depth: 0,
  })

  // Add all child folders to map
  childFolders.forEach(folder => {
    folderMap.set(folder._id.toString(), {
      ...folder,
      children: [],
      depth: 0, // Will be calculated later
    })
  })

  // Build tree structure and calculate depth
  childFolders.forEach(folder => {
    const parentId = folder.parent?.toString()
    if (parentId && folderMap.has(parentId)) {
      const parent = folderMap.get(parentId)
      const folderWithDepth = folderMap.get(folder._id.toString())
      folderWithDepth.depth = parent.depth + 1
      parent.children.push(folderWithDepth)
    }
  })

  // Sort children by name
  folderMap.forEach(folder => {
    if (folder.children.length > 0) {
      folder.children.sort((a, b) => a.name.localeCompare(b.name))
    }
  })

  return folderMap.get(rootFolder._id.toString())
}

// Recursive component to render folder and its children
const FolderItem = ({ folder, activeFolderId, onFolderSelect, bookmarkCounts, expandedFolders, toggleExpanded }) => {
  const isActive = folder._id.toString() === activeFolderId.toString()
  const isExpanded = expandedFolders.includes(folder._id.toString())
  const hasChildren = folder.children && folder.children.length > 0
  const bookmarkCount = bookmarkCounts[folder._id] || 0
  const depth = folder.depth || 0

  // Get folder icon or use emoji
  const folderIcon = folder.icon?.metadata?.native || null

  // Color mode values
  const activeBg = useColorModeValue('blue.50', 'blue.900')
  const activeColor = useColorModeValue('blue.600', 'blue.200')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')
  const badgeBg = useColorModeValue(isActive ? 'blue.100' : 'gray.100', isActive ? 'blue.800' : 'gray.700')
  const badgeColor = useColorModeValue(isActive ? 'blue.700' : 'gray.600', isActive ? 'blue.200' : 'gray.300')

  const MotionFlex = motion(Flex)

  return (
    <Box>
      <Tooltip label={folder.description} placement="top" hasArrow isDisabled={!folder.description}>
        <MotionFlex
          css={styles.folderItem(isActive, depth)}
          onClick={() => onFolderSelect(folder._id)}
          bg={isActive ? activeBg : 'transparent'}
          color={isActive ? activeColor : 'inherit'}
          _hover={{ bg: isActive ? activeBg : hoverBg }}
          whileTap={{ scale: 0.98 }}
        >
          <Box css={styles.chevronContainer} onClick={e => hasChildren && toggleExpanded(folder._id, e)}>
            {hasChildren ? (
              <Icon
                as={isExpanded ? ChevronDown : ChevronRight}
                boxSize={4}
                color={isActive ? activeColor : 'gray.500'}
              />
            ) : (
              <Box w={4} />
            )}
          </Box>

          <Icon
            as={isActive || isExpanded ? FolderOpen : Folder}
            boxSize={4}
            mr={2}
            color={isActive ? activeColor : 'gray.500'}
          />

          {folderIcon && (
            <Text mr={2} fontSize="14px">
              {folderIcon}
            </Text>
          )}

          <Text css={styles.folderName} fontWeight={isActive ? '600' : '400'}>
            {folder.name}
          </Text>
        </MotionFlex>
      </Tooltip>

      {hasChildren && isExpanded && (
        <Box css={styles.childrenContainer}>
          {folder.children.map(child => (
            <FolderItem
              key={child._id}
              folder={child}
              activeFolderId={activeFolderId}
              onFolderSelect={onFolderSelect}
              bookmarkCounts={bookmarkCounts}
              expandedFolders={expandedFolders}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

const FolderTree = ({ rootFolder, childFolders, activeFolderId, onFolderSelect, bookmarkCounts }) => {
  // State to track expanded folders
  const [expandedFolders, setExpandedFolders] = useState([rootFolder._id.toString()])

  // Toggle folder expanded state
  const toggleExpanded = (folderId, e) => {
    e.stopPropagation()
    setExpandedFolders(prev =>
      prev.includes(folderId.toString())
        ? prev.filter(id => id !== folderId.toString())
        : [...prev, folderId.toString()]
    )
  }

  // Build folder tree structure
  const folderTree = useMemo(() => {
    return buildFolderTree(rootFolder, childFolders)
  }, [rootFolder, childFolders])

  // Auto-expand parent folders of active folder
  useMemo(() => {
    if (activeFolderId === rootFolder._id.toString()) return

    // Find the active folder and its ancestors
    const findPath = (folder, targetId, path = []) => {
      if (folder._id.toString() === targetId) {
        return [...path, folder._id.toString()]
      }

      if (folder.children) {
        for (const child of folder.children) {
          const result = findPath(child, targetId, [...path, folder._id.toString()])
          if (result) return result
        }
      }

      return null
    }

    const path = findPath(folderTree, activeFolderId)
    if (path) {
      setExpandedFolders(prev => {
        const newExpanded = new Set([...prev, ...path])
        return Array.from(newExpanded)
      })
    }
  }, [activeFolderId, folderTree, rootFolder._id])

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.700')

  return (
    <Box css={styles.folderContainer} pb={4}>
      <Flex css={styles.folderHeader}>
        <Text css={styles.folderTitle}>Folders</Text>
        {rootFolder.isPublic && (
          <Flex align="center" fontSize="sm" color="gray.500">
            <Icon as={ExternalLink} boxSize={3} mr={1} />
            <Text fontSize="xs">Public</Text>
          </Flex>
        )}
      </Flex>

      <Box
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
        p={2}
        bg={useColorModeValue('white', 'gray.800')}
      >
        {folderTree ? (
          <FolderItem
            folder={folderTree}
            activeFolderId={activeFolderId}
            onFolderSelect={onFolderSelect}
            bookmarkCounts={bookmarkCounts}
            expandedFolders={expandedFolders}
            toggleExpanded={toggleExpanded}
          />
        ) : (
          <Text css={styles.emptyText}>No folders found</Text>
        )}
      </Box>
    </Box>
  )
}

export default memo(FolderTree)
